import { LanguageCodeMap, LanguagesEnum, ReverseLanguageCodeMap } from "constants/constants";
import {
    IDefaultFailureCodeTranslations,
    IDefaultFailureTypeTranslations,
    IDefaultKeyMeasureMetricTranslations,
    IDefaultKeySuccessFactorTranslations,
    IDefaultPriorityTranslations,
    IDefaultRCAFrequencyTriggerUnitTranslations,
    IDefaultStatusTranslations,
    IDefaultUserTypeTranslations,
    IFailureCode,
    IFailureType,
} from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { ISelectedTrendGraphItem } from "contexts/TrendReportContext/TrendReportContextInterfaces";
import { IPriority } from "entities/RCA/PriorityEntity/PriorityEntityInterfaces";
import { IRCAFrequencyTriggerUnit } from "entities/RCA/RCASettingsEntity/RCASettingsEntityInterfaces";
import { IStatus } from "entities/RCA/StatusEntity/StatusEntityInterfaces";
import { IDefaultFailureType } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { IUserType } from "entities/Users/UsersEntityInterfaces";
import {
    IDay,
    IDefaultDayTranslations,
    IKeySuccessFactors,
    IMeasureMetric,
} from "pages/Corporate/CorporateSetupDashboard/Corporations/MillSettingsStep/MillSettingsStepAPIInterfaces";
import { ILanguage } from "components/LanguagesDropdown/LanguagesInterfaces";

export interface IDefaultEmailNotificationTypeTranslations {
    notification_type_id: number;
    language: ILanguage;
    translated_type: string;
}

type DefaultTranslations =
    | IDefaultFailureTypeTranslations[]
    | IDefaultFailureCodeTranslations[]
    | IDefaultDayTranslations[]
    | IDefaultKeySuccessFactorTranslations[]
    | IDefaultKeyMeasureMetricTranslations[]
    | IDefaultUserTypeTranslations[]
    | IDefaultStatusTranslations[]
    | IDefaultPriorityTranslations[]
    | IDefaultEmailNotificationTypeTranslations[];

type Entity =
    | IFailureType
    | IDefaultFailureType
    | IFailureCode
    | ISelectedTrendGraphItem
    | IDay
    | IKeySuccessFactors
    | IMeasureMetric
    | IUserType
    | IStatus
    | IPriority
    | IRCAFrequencyTriggerUnit
    | IDefaultRCAFrequencyTriggerUnitTranslations
    | IDefaultEmailNotificationTypeTranslations;

type IBaseTranslation = {
    language: {
        language_id: number;
    };
    [key: string]: any;
};

export const translateDefaultEntity = <T extends Entity>(
    language: string,
    entity: T | undefined,
    textTag: keyof T,
    defaultTranslationField: keyof T,
): string => {
    const usableLanguageCode = language.slice(0, 2);

    if (!entity) {
        return "";
    }
    if (!defaultTranslationField || !entity[defaultTranslationField]) {
        return entity ? (entity[textTag] as string) : "";
    }
    const languageId = LanguageCodeMap[usableLanguageCode as keyof typeof LanguageCodeMap];
    if (!languageId) {
        return entity ? (entity[textTag] as string) : "";
    }

    const translations = entity[defaultTranslationField] as DefaultTranslations;
    if (!translations || translations.length === 0) {
        return entity[textTag] as string;
    }

    const targetTranslation = (translations as IBaseTranslation[]).find(
        (translation: IBaseTranslation) => translation.language.language_id === languageId,
    );
    if (!targetTranslation) {
        return entity[textTag] as string;
    }

    return targetTranslation[`translated_${String(textTag)}` as keyof typeof targetTranslation] as string;
};

export const handleNameTranslation = (
    language: string,
    translations: { name: string; language_id: number }[],
): string => {
    const usableLanguageCode = language.slice(0, 2);

    if (!translations.length) {
        return "";
    }

    const languageId = LanguageCodeMap[usableLanguageCode as keyof typeof LanguageCodeMap];
    if (!languageId) {
        const defaultEnglishTranslation = translations.find((t) => t.language_id === LanguagesEnum.EN);

        if (!defaultEnglishTranslation) {
            const languageCode =
                ReverseLanguageCodeMap[translations[0].language_id as keyof typeof ReverseLanguageCodeMap];

            if (!languageCode) {
                return translations[0].name;
            }

            return `${translations[0].name} (${languageCode.toLocaleUpperCase()})`;
        }

        return defaultEnglishTranslation.name;
    }

    const translation = translations.find((t) => t.language_id === languageId);
    if (!translation) {
        const languageCode = ReverseLanguageCodeMap[translations[0].language_id as keyof typeof ReverseLanguageCodeMap];

        if (!languageCode) {
            return translations[0].name;
        }

        return `${translations[0].name} (${languageCode.toLocaleUpperCase()})`;
    }

    return translation.name;
};
